import React, { createRef, Fragment, PropsWithChildren } from 'react'
import ReactToPrint from 'react-to-print' // ← NEW

import { Language, WSPayloadType, WSPublishPayload } from '../globals'
import { ReactComponent as MenuIcon } from '../assets/alternative_menu.svg'
import { ReactComponent as CloseIcon } from '../assets/close.svg'
import { ReactComponent as EndChatIcon } from '../assets/end_chat.svg'
import { ReactComponent as MinimizeIcon } from '../assets/minimize.svg'
import MuteIcon from '../assets/MuteIcon'
import SpeakerIcon from '../assets/SpeakerIcon'
import { ReactComponent as StartNewChatIcon } from '../assets/start_new_chat.svg'
import LocalizationContext from '../contexts/Localization/LocalizationContext'
import LanguageSwitch from './LanguageSwitch'
import { LanguageSwitchProps } from './LanguageSwitch/LanguageSwitch'
import ExpandIcon from '../assets/ExpandIcon'
import ContractIcon from '../assets/ContractIcon'
import { PrintMenuItem } from './PrintMenuItem'
import { ReactComponent as AboutIcon } from '../assets/about.svg'
import { ReactComponent as InoviaLogo } from '../assets/inovia_logo.svg'
import { version as appVersion } from '../../package.json'

const DEAFULT_STATE = {
  isMenuOpen: false,
  isModalOpen: false,
  shouldResetChat: false,
  isAboutOpen: false // ← NEW
}

const TEMP_SESSION_ID = 'TEMP_SESSION_ID'

type HeaderProps = {
  languageSwitchProps: Omit<
    LanguageSwitchProps,
    'modalRootRef' | 'hideMenu'
  > & {
    isSwitchVisible?: boolean
  }
  newSessionBtnProps: {
    isNSBVisible?: boolean
    onClick: () => void
    disabled?: boolean
  }
  onMinimizeBtnClick?: () => void
  setSessionId: (newSessionId: string | null, cb: () => void) => void
  wsPublish: (payload: Partial<WSPublishPayload>) => Promise<void>
  changeAudioStatus: (isMuted: boolean) => void

  channel?: string
  transcriptionDisabled?: boolean
  isMuted: boolean
  unlockAudio: () => void
  isExpanded: boolean // 🆕 read-only flag
  onToggleExpand: () => void
  stopChat: () => void

  printTargetRef?: React.RefObject<HTMLElement> // ← NEW

  // ← NEW (optional). If absent, the About item is hidden.
  about?: {
    version?: string

    // optional override for dialog title
    title?: string
  }
}

class Header extends React.Component<PropsWithChildren<HeaderProps>> {
  static contextType = LocalizationContext
  context!: React.ContextType<typeof LocalizationContext>

  state = DEAFULT_STATE

  modalRootRef = createRef<HTMLDivElement>()
  menuRef = React.createRef<HTMLDivElement>()
  menuTriggerRef = React.createRef<HTMLButtonElement>()
  firstItemRef = React.createRef<HTMLButtonElement>()
  lastItemRef = React.createRef<HTMLButtonElement>()
  headerRootRef = createRef<HTMLDivElement>()

  private lastWasKeyboard = false
  private onKeyDown = (e: KeyboardEvent) => {
    // treat Tab / arrows / Enter / Space as keyboard navigation
    const keys = [
      'Tab',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Enter',
      ' '
    ]
    if (keys.includes(e.key)) this.lastWasKeyboard = true

    this.onMenuKeyDown(e)
  }
  private onMouseDown = () => {
    this.lastWasKeyboard = false
  }

  private onFocusIn = (e: FocusEvent) => {
    const t = e.target as HTMLElement
    if (!t) return
    // limit to header buttons
    if (t.matches('.btn-header, .header-menu .btn')) {
      // if (t.matches('.btn-header, .header-menu .btn')) {
      //   if (this.lastWasKeyboard) {
      //     t.style.outline = '2px solid var(--primary-color)'
      //     t.style.outlineOffset = '2px'
      //     t.style.boxShadow = '0 0 0 3px rgba(0,0,0,.25)'
      //   } else {
      //     t.style.outline = ''
      //     t.style.outlineOffset = ''
      //     t.style.boxShadow = ''
      //   }
      // }

      // if (this.lastWasKeyboard)
      t.classList.add('kbd-focus')
      // if it got focus via mouse/touch, ensure it’s clean
      // else t.classList.remove('kbd-focus')
    }
  }

  private onFocusOut = (e: FocusEvent) => {
    const t = e.target as HTMLElement
    if (this.state.isMenuOpen) {
      return
    }
    if (t?.matches('.btn-header, .header-menu .btn')) {
      t.classList.remove('kbd-focus')
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown, true)
    document.addEventListener('mousedown', this.onMouseDown, true)
    // (optional) treat touch as pointer/mouse
    document.addEventListener('pointerdown', this.onMouseDown, true)

    const root = this.headerRootRef.current
    if (root) {
      root.addEventListener('focusin', this.onFocusIn as any, true)
      root.addEventListener('focusout', this.onFocusOut as any, true)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown, true)
    document.removeEventListener('mousedown', this.onMouseDown, true)
    document.removeEventListener('pointerdown', this.onMouseDown, true)

    const root = this.headerRootRef.current
    if (root) {
      root.removeEventListener('focusin', this.onFocusIn as any, true)
      root.removeEventListener('focusout', this.onFocusOut as any, true)
    }
  }

  stopChat = async () => {
    this.props.stopChat?.()
    this.setState(DEAFULT_STATE)
  }

  openMenu = () => {
    this.setState({ isMenuOpen: true }, () => {
      this.menuRef.current?.removeAttribute('inert')
      this.menuRef.current?.setAttribute('aria-hidden', 'false')
      this.focusFirstTabbable()
    })
  }

  closeMenu = () => {
    this.setState({ isMenuOpen: false }, () => {
      this.menuRef.current?.setAttribute('inert', '')
      this.menuRef.current?.setAttribute('aria-hidden', 'true')
      this.menuTriggerRef.current?.focus()
    })
  }

  private getTabbables = (): HTMLElement[] => {
    const root = this.menuRef.current
    if (!root) return []
    return Array.from(
      root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    )
  }

  private focusFirstTabbable = () => {
    const first = this.getTabbables()[0]
    first?.focus()
  }

  private focusLastTabbable = () => {
    const tabbables = this.getTabbables()
    const last = tabbables[tabbables.length - 1]
    last?.focus()
  }

  onMenuKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!this.state.isMenuOpen) return // only handle when OPEN

    if (e.key === 'Escape') {
      e.preventDefault()
      this.closeMenu()
      return
    }

    if (e.key !== 'Tab') return

    const tabbables = this.getTabbables()
    console.debug(
      'tabbables',
      tabbables.map((n) => n.tagName + '.' + (n.className || ''))
    )
    if (!tabbables.length) return

    const first = tabbables[0]
    const last = tabbables[tabbables.length - 1]
    const active = document.activeElement as HTMLElement | null

    if (e.shiftKey) {
      if (active === first || !tabbables.includes(active!)) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (active === last || !tabbables.includes(active!)) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  render() {
    const { getLocalizedString } = this.context
    const {
      children,
      languageSwitchProps: { isSwitchVisible, ...languageSwitchProps },
      newSessionBtnProps: { isNSBVisible, ...newSessionBtnProps },
      onMinimizeBtnClick,
      isMuted,
      changeAudioStatus,
      channel,
      unlockAudio,
      about,
      transcriptionDisabled
    } = this.props
    const { isMenuOpen, isModalOpen, shouldResetChat, isAboutOpen } = this.state

    const listOfChannels = { test: 'Test' }

    const setIsModalOpen = (isModalOpen: boolean) =>
      this.setState({ isModalOpen })
    const setShouldResetChat = (chatResetStatus: boolean) =>
      this.setState({ shouldResetChat: chatResetStatus })

    return (
      <Fragment>
        {/* Backdrop */}
        <div
          className={`header-menu-bg ${isMenuOpen ? 'open' : ''}`}
          onClick={this.closeMenu}
        />

        <div className="chatui-header" ref={this.headerRootRef}>
          <div className="side-buttons">
            {/* expand / collapse */}
            <button
              className="btn-header btn-expand-toggle"
              onClick={this.props.onToggleExpand}
              title={this.props.isExpanded ? 'Shrink' : 'Expand'}
            >
              {this.props.isExpanded ? <ContractIcon /> : <ExpandIcon />}
            </button>

            {/* menu trigger */}
            <button
              ref={this.menuTriggerRef}
              className={`btn-header ${isMenuOpen ? 'open' : ''}`}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-controls="chat-header-menu"
              onClick={() => {
                unlockAudio()
                isMenuOpen ? this.closeMenu() : this.openMenu()
              }}
              title="Menu"
            >
              {this.props.languageSwitchProps.activeLanguage?.menuText ?? (
                <MenuIcon />
              )}
            </button>
          </div>

          <div
            className="brand-identity"
            title={languageSwitchProps.activeLanguage?.label}
          >
            {children}
          </div>

          {transcriptionDisabled != true ? (
            <>
              <div
                className="side-buttons"
                style={{ display: isMuted ? 'none' : 'flex' }}
              >
                <button
                  title="Read Message Aloud"
                  className="btn-header"
                  onClick={() => {
                    changeAudioStatus(true)
                  }}
                >
                  {channel === listOfChannels.test ? (
                    <CloseIcon />
                  ) : (
                    <SpeakerIcon />
                  )}
                </button>
              </div>

              <div
                className="side-buttons"
                style={{ display: isMuted ? 'flex' : 'none' }}
              >
                <button
                  title="Mute"
                  className="btn-header"
                  onClick={() => {
                    changeAudioStatus(false)
                  }}
                >
                  {channel === listOfChannels.test ? (
                    <CloseIcon />
                  ) : (
                    <MuteIcon />
                  )}
                </button>
              </div>
            </>
          ) : null}
          <div className="side-buttons">
            <button
              title="Minimize"
              className="btn-header"
              onClick={onMinimizeBtnClick}
            >
              {channel === listOfChannels.test ? (
                <CloseIcon />
              ) : (
                <MinimizeIcon />
              )}
            </button>
          </div>

          {/* MENU */}
          <div
            id="chat-header-menu"
            ref={this.menuRef}
            className={`header-menu ${isMenuOpen ? 'open' : ''}`}
            role="menu"
            aria-hidden={!isMenuOpen}
            onKeyDown={this.onMenuKeyDown}
            /* when CLOSED, keep background focusable but block pointer hits via your CSS;
   if you still want to hard-block focus, add inert here when !isMenuOpen */
            {...(!isMenuOpen ? ({ inert: '' } as any) : {})}
          >
            <ul>
              {isSwitchVisible && (
                <LanguageSwitch
                  {...languageSwitchProps}
                  hideMenu={this.closeMenu}
                  modalRootRef={this.modalRootRef.current}
                  activeLanguage={languageSwitchProps.activeLanguage}
                />
              )}

              {isNSBVisible && (
                <li>
                  <button
                    className="btn"
                    disabled={newSessionBtnProps.disabled}
                    onClick={() => {
                      newSessionBtnProps.onClick()
                      this.closeMenu()
                    }}
                    role="menuitem"
                  >
                    <StartNewChatIcon className="menu-icon" />
                    <span className="menu-main-text">
                      {getLocalizedString('newSession', 'Start new chat')}
                    </span>
                  </button>
                </li>
              )}

              <li>
                <PrintMenuItem
                  contentRef={
                    this.props.printTargetRef as React.RefObject<HTMLElement>
                  }
                  onAfterPrint={() => this.setState({ isMenuOpen: false })}
                />
              </li>

              {about && (
                <li>
                  <button
                    className="btn"
                    onClick={() => {
                      this.setState({ isAboutOpen: true })
                      this.closeMenu()
                    }}
                    role="menuitem"
                  >
                    <AboutIcon className="menu-icon" />
                    <span className="menu-main-text">
                      {getLocalizedString('about', 'About')}
                    </span>
                  </button>
                </li>
              )}

              <li>
                <button
                  className="btn"
                  onClick={() => {
                    setIsModalOpen(true)
                    setShouldResetChat(true)
                  }}
                  role="menuitem"
                >
                  <EndChatIcon className="menu-icon" />
                  <span className="menu-main-text">
                    {getLocalizedString('stopChat', 'Stop chat')}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Stop Chat Modal */}
        {isModalOpen && shouldResetChat && (
          <div className="header-modal" role="dialog" aria-modal="true">
            <div className="header-modal-body">
              <button
                className="close-btn"
                onClick={() => setIsModalOpen(false)}
              >
                <CloseIcon />
              </button>
              {getLocalizedString(
                'stopChatPrompt',
                'Do you really want to leave current chat?'
              )}
              <button className="stop-btn" onClick={this.stopChat}>
                {getLocalizedString('stopChat', 'Stop chat')}
              </button>
            </div>
          </div>
        )}

        {/* About Modal */}
        {isAboutOpen && about && (
          <div className="header-modal" role="dialog" aria-modal="true">
            <div className="header-modal-body about-modal">
              <button
                className="close-btn"
                onClick={() => this.setState({ isAboutOpen: false })}
                aria-label={getLocalizedString('close', 'Close')}
              >
                <CloseIcon />
              </button>

              <div className="about-rows">
                <div className="about-powered">
                  <span className="powered-label">{'Powered by'}</span>
                  <InoviaLogo className="about-logo" aria-label="Inovia" />
                </div>

                <p className="about-line">
                  <strong>{'Version'}:</strong> {about.version ?? appVersion}
                </p>
              </div>
            </div>
          </div>
        )}

        <div ref={this.modalRootRef} />
      </Fragment>
    )
  }
}

export default Header
